/* iTechVista — find anything, from anywhere
   ---------------------------------------------------------------------------
   PROTOTYPE. Not wired into the theme. See rebuild/docs/search/README.txt.

   THE PROBLEM THIS IS FOR

   The header carries 277 links and 1,179 DOM nodes — 69% of an entire page —
   and is rendered twice, once for desktop and once for mobile. That is what
   happens when navigation is asked to be a catalogue. With 339 tools across
   15 categories no menu can be both complete and usable, and the current one
   pays 39% extra page weight to be neither.

   A menu answers "what is there?". Nobody arrives at a store of 339 tools
   with that question. They arrive with "is there one that does X", which is a
   FINDING problem, and finding is what a search box is for.

   There is already a client-side search over the same index, but it lives on
   one page — you have to find the finder first. This is the same data as a
   front door: available from every page, opened with a keystroke, and never
   fetched at all until someone asks for it.

   WHAT IT COSTS UNTIL SOMEONE USES IT
   One keydown listener. The 87KB index is fetched on first open and never
   again; the palette's own markup is built at that moment too. A visitor who
   never presses the key pays for a single event listener and nothing else.

   RANKING, BECAUSE A FRONT DOOR NEEDS IT
   The existing page filter does a substring scan, which is right for a filter
   — you are looking at the whole list anyway. It is wrong for a front door,
   where the first result has to be the obvious one. Typing "json" should not
   lead with something whose description happens to mention JSON while
   `json-formatter-validator` sits ninth.

   So matches are scored by WHERE they hit and how specific the hit is: a
   title that starts with the query beats a title that merely contains it,
   which beats a category, which beats a description. Every term in a
   multi-word query must match something, or the result is dropped. */
(function () {
  'use strict';

  var TRIGGER_KEY = '/';           // and Ctrl/Cmd-K
  var MAX_RESULTS = 12;

  var script = document.currentScript;
  var INDEX_URL = (script && script.dataset && script.dataset.index) || '';
  var CSS_URL = (script && script.dataset && script.dataset.css) || '';

  /* The palette carries its own tokens rather than reading --color-* , because
     70 tool pages declare those on `body` and would repaint an overlay that
     borrowed them. So the THEME has to say which palette to use, once, here --
     it cannot be inferred safely from the page. */
  try {
    var pal = script && script.dataset && script.dataset.theme;
    if (pal) document.documentElement.setAttribute('data-itvsp-theme', pal);
  } catch (e) { /* ignore */ }

  var tools = null;        // loaded on first open
  var loading = false;
  var el = null;           // the palette, built on first open
  var returnTo = null;
  var active = -1;
  var results = [];

  /* ------------------------------------------------------------- matching */

  /* Fold to something forgiving: lowercase, and hyphens/underscores to
     spaces, so "json to csv", "json-to-csv" and "jsontocsv" all behave.
     Punctuation is dropped rather than kept, because a query is typed in a
     hurry and nobody types "(Beta)". */
  function fold(s) {
    return String(s || '').toLowerCase()
      .replace(/[‐-―]/g, '-')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function prepare(rows) {
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      r._t = fold(r.t);
      r._c = fold(r.c);
      r._d = fold(r.d);
      r._squash = r._t.replace(/ /g, '');   // catches "jsontocsv"
    }
    return rows;
  }

  /* Score one tool against one term. 0 means no match at all, and one
     unmatched term disqualifies the whole row — an AND, not an OR, because
     "pdf merge" should not return every PDF tool and every merge tool. */
  function scoreTerm(r, term) {
    if (r._t === term) return 1000;
    if (r._t.indexOf(term) === 0) return 600;
    if (r._squash.indexOf(term) === 0) return 550;

    // a word inside the title starting with the term: "excel" in "MS Excel Pro"
    if (r._t.indexOf(' ' + term) !== -1) return 400;
    if (r._t.indexOf(term) !== -1) return 250;
    if (r._squash.indexOf(term) !== -1) return 200;

    if (r._c.indexOf(term) !== -1) return 90;
    if (r._d.indexOf(' ' + term) !== -1) return 45;
    if (r._d.indexOf(term) !== -1) return 30;
    return 0;
  }

  function search(query) {
    var q = fold(query);
    if (!q) return [];
    var terms = q.split(' ');
    var out = [];

    for (var i = 0; i < tools.length; i++) {
      var r = tools[i], total = 0, ok = true;
      for (var j = 0; j < terms.length; j++) {
        var sc = scoreTerm(r, terms[j]);
        if (!sc) { ok = false; break; }
        total += sc;
      }
      if (!ok) continue;

      /* A shorter title carrying the same match is the more specific tool:
         for "password", "Password Generator" should beat "Password Generator
         and Strength Auditor Suite". Small enough not to outrank a real
         difference in match quality. */
      total += Math.max(0, 40 - r._t.length / 2);
      out.push({ r: r, score: total });
    }

    out.sort(function (a, b) {
      return b.score - a.score || (a.r._t < b.r._t ? -1 : 1);
    });
    return out.slice(0, MAX_RESULTS).map(function (x) { return x.r; });
  }

  /* --------------------------------------------------------------- markup */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Operates on already-escaped text, and escapes the needle, so a query
  // cannot inject markup.
  function mark(text, query) {
    var safe = esc(text);
    var q = String(query || '').trim();
    if (!q) return safe;
    try {
      var re = new RegExp('(' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
      return safe.replace(re, '<mark>$1</mark>');
    } catch (e) {
      return safe;
    }
  }

  function build() {
    el = document.createElement('div');
    el.className = 'itvsp';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Search tools');
    el.innerHTML =
      '<div class="itvsp__box">' +
        '<div class="itvsp__bar">' +
          '<svg class="itvsp__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
            '<circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" stroke-width="2"/>' +
            '<path d="M13.5 13.5 18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
          '</svg>' +
          '<input class="itvsp__input" type="text" autocomplete="off" spellcheck="false" ' +
                 'placeholder="Search 339 tools" aria-label="Search tools" ' +
                 'role="combobox" aria-expanded="false" aria-controls="itvsp-list" ' +
                 'aria-autocomplete="list">' +
          '<button class="itvsp__close" type="button" aria-label="Close search">Esc</button>' +
        '</div>' +
        '<ul class="itvsp__list" id="itvsp-list" role="listbox" aria-label="Results"></ul>' +
        '<p class="itvsp__hint">' +
          '<span><kbd>&uarr;</kbd><kbd>&darr;</kbd> to move</span>' +
          '<span><kbd>Enter</kbd> to open</span>' +
          '<span><kbd>Esc</kbd> to close</span>' +
        '</p>' +
      '</div>';
    document.body.appendChild(el);

    var input = el.querySelector('.itvsp__input');
    var list = el.querySelector('.itvsp__list');

    input.addEventListener('input', function () { render(input.value); });
    el.querySelector('.itvsp__close').addEventListener('click', close);

    // the backdrop closes; the box does not
    el.addEventListener('mousedown', function (ev) {
      if (ev.target === el) close();
    });

    // a real mouse movement hands control back to the pointer
    el.addEventListener('mousemove', function () {
      el.classList.remove('itvsp--keys');
    });

    list.addEventListener('click', function (ev) {
      var a = ev.target.closest && ev.target.closest('a');
      if (a) close(true);
    });
  }

  function render(query) {
    var list = el.querySelector('.itvsp__list');
    var input = el.querySelector('.itvsp__input');
    results = query.trim() ? search(query) : [];
    active = results.length ? 0 : -1;

    if (!query.trim()) {
      list.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      el.classList.remove('is-empty');
      return;
    }

    if (!results.length) {
      list.innerHTML = '<li class="itvsp__none">No tool matches ' +
                       '&ldquo;' + esc(query.trim()) + '&rdquo;.</li>';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      return;
    }

    var html = '';
    for (var i = 0; i < results.length; i++) {
      var r = results[i];
      html += '<li role="option" id="itvsp-o' + i + '" aria-selected="' +
              (i === active ? 'true' : 'false') + '"' +
              (i === active ? ' class="is-active"' : '') + '>' +
                '<a href="/pages/' + esc(r.h) + '">' +
                  '<span class="itvsp__t">' + mark(r.t, query) + '</span>' +
                  '<span class="itvsp__c">' + esc(r.c) + '</span>' +
                '</a>' +
              '</li>';
    }
    list.innerHTML = html;
    input.setAttribute('aria-expanded', 'true');
    input.setAttribute('aria-activedescendant', 'itvsp-o' + active);
  }

  function move(delta) {
    if (!results.length) return;
    /* The arrow keys take over: see the .itvsp--keys note in the stylesheet.
       Two highlighted rows -- one the keyboard is on, one the mouse is merely
       resting over -- reads as a bug, so the last device used wins. */
    el.classList.add('itvsp--keys');
    var list = el.querySelector('.itvsp__list');
    var input = el.querySelector('.itvsp__input');
    var items = list.querySelectorAll('li[role="option"]');
    if (active >= 0 && items[active]) {
      items[active].classList.remove('is-active');
      items[active].setAttribute('aria-selected', 'false');
    }
    active = (active + delta + results.length) % results.length;
    items[active].classList.add('is-active');
    items[active].setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', 'itvsp-o' + active);

    /* The caret stays in the input the whole time — that is the point of
       aria-activedescendant. Moving real focus into the list would mean
       every arrow key press interrupts typing. */
    items[active].scrollIntoView({ block: 'nearest' });
  }

  /* ---------------------------------------------------------- open / close */

  function withCss(next) {
    if (!CSS_URL) return next();
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CSS_URL;
    link.addEventListener('load', next);
    link.addEventListener('error', next);   // unstyled beats nothing
    document.head.appendChild(link);
  }

  function open() {
    if (el && el.classList.contains('is-open')) return;
    returnTo = document.activeElement;

    function show() {
      el.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      var input = el.querySelector('.itvsp__input');
      /* Same lesson as the dialog in itv-kit.js: at the instant the class
         changes the computed visibility is still hidden, and focus() on a
         hidden element does nothing at all. Ask again next frame. */
      (function tryFocus(n) {
        try { input.focus(); } catch (e) { /* ignore */ }
        if (document.activeElement !== input && n < 30) {
          requestAnimationFrame(function () { tryFocus(n + 1); });
        }
      })(0);
      input.select();
    }

    function ready() {
      if (!el) build();
      show();
    }

    if (tools) return ready();
    if (loading) return;
    loading = true;

    withCss(function () {
      fetch(INDEX_URL).then(function (r) { return r.json(); }).then(function (rows) {
        tools = prepare(rows);
        loading = false;
        ready();
      }).catch(function () {
        loading = false;
        /* If the index cannot be fetched there is nothing useful to show, and
           an empty box would be worse than not opening. Fall back to the
           store's own search page rather than swallowing the keystroke. */
        window.location.href = '/search';
      });
    });
  }

  function close(navigating) {
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    if (navigating) return;      // the page is leaving; do not fight it
    try {
      if (returnTo && returnTo.isConnected && returnTo.focus) returnTo.focus();
    } catch (e) { /* ignore */ }
  }

  /* ------------------------------------------------------------- keyboard */

  function typingInSomethingElse(t) {
    if (!t) return false;
    var tag = (t.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' ||
           t.isContentEditable === true;
  }

  document.addEventListener('keydown', function (ev) {
    try {
      var isOpen = el && el.classList.contains('is-open');

      if (!isOpen) {
        /* "/" is the convention, but these are TOOL pages — a good number of
           them are text editors, regex testers and terminals, and stealing a
           printable character inside one would be its own bug. So "/" only
           counts when the visitor is not typing into something, while
           Ctrl/Cmd-K works anywhere. */
        var wantsKey = ev.key === TRIGGER_KEY && !typingInSomethingElse(ev.target) &&
                       !ev.ctrlKey && !ev.metaKey && !ev.altKey;
        var wantsCombo = (ev.ctrlKey || ev.metaKey) && (ev.key === 'k' || ev.key === 'K');
        if (wantsKey || wantsCombo) { ev.preventDefault(); open(); }
        return;
      }

      if (ev.key === 'Escape') { ev.preventDefault(); close(); return; }
      if (ev.key === 'ArrowDown') { ev.preventDefault(); move(1); return; }
      if (ev.key === 'ArrowUp') { ev.preventDefault(); move(-1); return; }
      if (ev.key === 'Home' && results.length) { ev.preventDefault(); move(-active); return; }

      if (ev.key === 'Enter' && active >= 0 && results[active]) {
        ev.preventDefault();
        window.location.href = '/pages/' + results[active].h;
        return;
      }

      if (ev.key === 'Tab') {
        // a modal keeps its own focus; there are only two stops in here
        var stops = el.querySelectorAll('input, button');
        var first = stops[0], last = stops[stops.length - 1];
        if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
        else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
      }
    } catch (e) { /* a search box must never break the page under it */ }
  }, true);

  // Anything the page marks opens it too, so a header button can hook in
  // without this file knowing anything about the header.
  document.addEventListener('click', function (ev) {
    var t = ev.target.closest && ev.target.closest('[data-itv-search-open]');
    if (t) { ev.preventDefault(); open(); }
  });

  window.itvSearch = { open: open, close: close };
})();
